import { useEffect, useMemo, useState } from 'react';
import Card from './Card';
import { CategoryInterface } from '../../../services/exports/Interfaces';
import SideBarIcon from '../../../assets/logo/SideBarIcon';
import useTheme from '../../../hooks/ui/useTheme';
import MenuSideBar from './MenuSideBar';
import useMenu from '../../../hooks/menu/useMenu';
import SearchInput from './SearchInput/SearchInput';
import classNames from 'classnames';
import useScreenType from '../../../hooks/utility/useScreenType';
import ScrollMenu from './ScrollMenu';
import { useSearchParams } from 'react-router-dom';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { collect } from 'collect.js';
import useUrlParams from '../../../hooks/utility/useUrlParams';
import MenuBarSearch from './MenuBarSearch';
import { useMenuCategoryList } from '../category-list/MenuCategoryListProvider';

const MenuBar = () => {
  const { menus, selectedMenu } = useMenu();
  const { filteredCategories: categories, focusedCategoryId, menuBarRef, jumpToCategory } = useMenuCategoryList();
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const { isDesktop, isMobile } = useScreenType();
  const { behavior, categoryId } = useUrlParams();
  const [searchParams] = useSearchParams();
  const hasSearchInput = isDesktop || (!isDesktop && searchParams.has('search'));
  const hasCategories = !!categories?.length && (!isMobile || (isMobile && !searchParams.has('search')));
  const { isMobileApp } = useMobileApp();

  useEffect(() => {
    selectedMenu && setSelectedMenuId(selectedMenu?.id);

    selectedMenuId &&
      selectedMenuId !== selectedMenu?.id &&
      menus.firstWhere('id', selectedMenuId) &&
      categories?.length > 0 &&
      jumpToCategory(categories[0].id);
  }, [selectedMenu?.id]);

  const handleHashChange = (): void => {
    new Promise((resolve, reject) => {
      if (!categoryId) {
        return reject(true);
      }

      jumpToCategory(categoryId, behavior);
      resolve(true);
    })
      .then(() => {
        window.history.replaceState({}, '', window.location.pathname);
      })
      .catch(() => {});
  };

  useEffect(() => {
    handleHashChange();
  }, [categoryId]);

  const activeCategoryId = useMemo(
    () => focusedCategoryId ?? collect(categories)?.first()?.id,
    [focusedCategoryId, categories],
  );

  return (
    <div
      className="bg-brand-background-primary relative h-fit pt-xs pb-sm flex flex-col space-y-sm lg:hidden"
      id="menu-bar"
      ref={menuBarRef}
    >
      {hasSearchInput && <SearchInput className={classNames({ 'px-sm': !isDesktop })} />}
      {hasCategories && (
        <div
          className={classNames(
            'h-full w-full flex flex-row items-center relative lg:px-none gap-xs',
            !isMobileApp && 'px-sm',
          )}
        >
          {!isMobileApp && <MenuBar.MenuButton />}
          {!isDesktop && !isMobileApp && <MenuBarSearch />}
          {categories?.length > 0 && (
            <ScrollMenu focusedCategoryId={activeCategoryId}>
              {categories?.map((category: CategoryInterface) => (
                <Card
                  key={category?.id?.toString()}
                  itemId={category?.id?.toString()}
                  category={category}
                  focusedCategoryId={activeCategoryId}
                  onClick={jumpToCategory}
                />
              ))}
            </ScrollMenu>
          )}
        </div>
      )}
    </div>
  );
};

MenuBar.MenuButton = () => {
  const { filteredCategories: categories, focusedCategoryId, jumpToCategory } = useMenuCategoryList();
  const { isMobile } = useScreenType();
  const theme = useTheme();
  const [showSideBar, setShowSideBar] = useState<boolean>(false);
  const toggleSideBar = (): void => setShowSideBar((prev) => !prev);

  return (
    <>
      <MenuSideBar
        categories={categories}
        open={showSideBar}
        toggle={setShowSideBar}
        focusedCategoryId={focusedCategoryId}
        onSelectCategory={(category) => jumpToCategory(category?.id)}
      />
      <button
        className={classNames(
          'flex flex-row p-2xs bg-brand-background-primary border border-brand-border rounded-full items-center justify-center shrink-0 size-[32px]',
          {
            'rounded-full size-8 border justify-center': isMobile,
          },
        )}
        onClick={toggleSideBar}
        id={'menu-button'}
      >
        <SideBarIcon color={theme.color.brand.icon.color} className="h-[12px]" />
      </button>
    </>
  );
};

export default MenuBar;
